import { ApiRequest } from 'app/common/types'
import { ApiGetResponse, ApiPagedResponse } from 'app/common/types/api'
import { InstalledConnection } from 'app/connections/types'
import { InstalledFacebookConnection } from 'app/connections/types/facebook'
import { InstalledGoogleAnalyticsConnection } from 'app/connections/types/googleAnalytics'
import { InstalledTiktokConnection } from 'app/connections/types/tiktok'

import { Treatment } from './treatment'

interface AudiencesGetContent {
  id?: string
  type: 'audience' // Temporary until we make the Frontend comaptible with signals in general
}

export type AudiencesGetRequest = ApiRequest<AudiencesGetContent>

export type AudienceStatus = 'active' | 'archieved' | 'new' | 'paused' | 'preparation'
export type AudienceType = 'exclusion' | 'lookalike' | 'retargeting' | 'search' | 'seed'
export type AudienceSignalPurpose = 'exclusion' | 'retargeting' | 'seed'
export type SignalTreatmentScope = 'campaign' | 'treatment'

export interface SignalConnectionAccount {
  id: string
  name: string
}

interface FacebookAudienceSignalConnection {
  platform: { id: string; name: 'facebook' }
  account: SignalConnectionAccount
}

interface CriteoAudienceSignalConnection {
  platform: { id: string; name: 'criteo' }
  account: SignalConnectionAccount
}

interface GoogleAnalyticsAudienceSignalConnection {
  platform: { id: string; name: 'googleAnalytics' }
  targetingEventName?: string
  targetingKey?: string
}

interface TiktokAudienceSignalConnection {
  platform: { id: string; name: 'tiktok' }
  account: SignalConnectionAccount
}

export type AudienceSignalConnection =
  | CriteoAudienceSignalConnection
  | FacebookAudienceSignalConnection
  | GoogleAnalyticsAudienceSignalConnection
  | TiktokAudienceSignalConnection

interface FacebookAudienceSignalRelatesTo {
  facebook: {
    lookalikes: {
      adAccountId: string
      adAccountName: string
      externalId: string
      name: string | null
      created: string | null
    }[]
  }
}

interface TiktokAudienceSignalRelatesTo {
  tiktok: {
    lookalikes: {
      name: string | null
      created: string | null
      externalId: string
      advertiserId: string
      advertiserName: string
    }[]
  }
}

interface CriteoAudienceSignalRelatesTo {
  criteo: {
    criteoContactListId: string
    lookalikes: {
      name: string | null
      created: string | null
      externalId: string
      advertiserId: string
      advertiserName: string
      type: 'AudienceSegment'
    }[]
  }
}

interface GoogleAnalyticsAudienceSignalRelatesTo {
  googleAdwords: {
    customerId: string
    customerName?: string
    userList: string
    name: string
    lookalikes: {
      customerId: string
      customerName: string
      externalId: string
      resourceName: string
      name: string
    }[]
  }[]
  dv360: {
    advertiserId: string
    advertiserName?: string
    firstPartyAndPartnerAudienceId: string
    name: string
    type: string
  }[]
}

export type AudienceSignalRelatesTo =
  | CriteoAudienceSignalRelatesTo
  | FacebookAudienceSignalRelatesTo
  | GoogleAnalyticsAudienceSignalRelatesTo
  | TiktokAudienceSignalRelatesTo

export interface AudienceSignalConfig {
  purpose: AudienceSignalPurpose
  lookbackDays: number
  size: number
  treatments: {
    scope: SignalTreatmentScope
    ids: string[]
  }
  isValueBased?: true
}

export interface AudienceEntity {
  id: string
  name: string
  externalId: string
  model: string | null
  type: 'audience'
  connection: AudienceSignalConnection
  config: AudienceSignalConfig
  status: AudienceStatus
  objective?: string
  createdBy: string | null
  createdAt: string
  updatedAt: string
  relatesTo: AudienceSignalRelatesTo
}

export type AudiencesGetResponse = ApiGetResponse<AudienceEntity[]> & ApiPagedResponse

export type RelatedAudience = {
  created: Date | null
  name: string
}

export type Audience = {
  account: SignalConnectionAccount | null
  config: {
    purpose: AudienceSignalPurpose
    treatments: string[]
  }
  created: Date
  createdBy?: string
  id: string
  name: string
  related: RelatedAudience[]
  connection: InstalledConnection | null
  status: AudienceStatus
}

export type AudienceConfigurationConnection =
  | InstalledFacebookConnection
  | InstalledGoogleAnalyticsConnection
  | InstalledTiktokConnection

export interface AudienceConfiguration {
  adAccountId?: string
  audienceKey: string
  description?: string
  name?: string
  pixelId?: string
  connection?: AudienceConfigurationConnection
  treatments: Treatment[]
  type?: AudienceType
}

const mapToRelatedAudience = (lookalike: {
  name: string | null
  created?: string | null
}): RelatedAudience => ({
  created: lookalike.created ? new Date(lookalike.created) : null,
  name: lookalike.name ?? '-',
})

const mapToRelatedAudiences = (relatesTo: AudienceSignalRelatesTo): RelatedAudience[] => {
  if ('facebook' in relatesTo) {
    return relatesTo.facebook.lookalikes.map(mapToRelatedAudience)
  }
  if ('tiktok' in relatesTo) {
    return relatesTo.tiktok.lookalikes.map(mapToRelatedAudience)
  }
  if ('criteo' in relatesTo) {
    return relatesTo.criteo.lookalikes.map(mapToRelatedAudience)
  }
  if ('googleAdwords' in relatesTo || 'dv360' in relatesTo) {
    return [
      ...(relatesTo.googleAdwords ?? []).flatMap((adwordsAudience) =>
        adwordsAudience.lookalikes.map((lookalike) => mapToRelatedAudience(lookalike)),
      ),
      ...(relatesTo.dv360 ?? []).map((dv360Audience) => mapToRelatedAudience(dv360Audience)),
    ]
  }

  return []
}

export const mapToAudience = (
  audienceEntity: AudienceEntity,
  connections: InstalledConnection[],
): Audience =>
  ({
    account: 'account' in audienceEntity.connection ? audienceEntity.connection.account : null,
    config: {
      purpose: audienceEntity.config.purpose,
      treatments: audienceEntity.config.treatments?.ids ?? [],
    },
    created: new Date(audienceEntity.createdAt ? audienceEntity.createdAt : 0),
    createdBy: audienceEntity.createdBy ?? undefined,
    id: audienceEntity.id,
    name: audienceEntity.name,
    related: audienceEntity.relatesTo ? mapToRelatedAudiences(audienceEntity.relatesTo) : [],
    connection: connections.find((c) => c.id === audienceEntity.connection.platform.id) ?? null,
    status: audienceEntity.status,
  }) satisfies Audience

export const mapToAudiences = (
  audienceEntities: AudienceEntity[],
  connections: InstalledConnection[],
): Audience[] =>
  audienceEntities.map((audienceEntity) => mapToAudience(audienceEntity, connections))
